import type { Key, ReactNode } from 'react';
import type { HistoryGraphLayout, HistoryGraphRow } from '@/lib/undoTree';
import { cn } from '@/lib/utils';

/** The graph's geometry, in pixels: a lane's width and a row's height. */
const LANE_WIDTH = 12;
const ROW_HEIGHT = 26;

const lineClass = (isActive: boolean) =>
  isActive ? 'stroke-primary' : 'stroke-muted-foreground/40';

/**
 * One row of the history graph: the lines passing through it, the lines from
 * the node up to its children (straight, or folding in from a branch's lane),
 * the line down to its parent, and the node's dot.
 */
function HistoryGraphCell<N>({
  row,
  laneCount,
  rowHeight,
  laneWidth,
}: {
  row: HistoryGraphRow<N>;
  laneCount: number;
  rowHeight: number;
  laneWidth: number;
}) {
  const laneX = (lane: number) => laneWidth / 2 + lane * laneWidth;
  const x = laneX(row.lane);
  const middle = rowHeight / 2;
  return (
    <svg
      aria-hidden
      width={laneCount * laneWidth}
      height={rowHeight}
      className="shrink-0 overflow-visible"
      fill="none"
      strokeWidth={1.5}
    >
      {row.through.map((line) => (
        <line
          key={`through-${line.fromLane}`}
          x1={laneX(line.fromLane)}
          x2={laneX(line.fromLane)}
          y1={0}
          y2={rowHeight}
          className={lineClass(line.isActive)}
        />
      ))}
      {row.edges.map((edge) => (
        <path
          key={`edge-${edge.fromLane}`}
          d={`M ${laneX(edge.fromLane)} 0 C ${laneX(edge.fromLane)} ${middle / 2}, ${x} ${middle / 2}, ${x} ${middle}`}
          className={lineClass(edge.isActive)}
        />
      ))}
      {row.hasParent && (
        <line
          x1={x}
          x2={x}
          y1={middle}
          y2={rowHeight}
          className={lineClass(row.isActive)}
        />
      )}
      <circle
        cx={x}
        cy={middle}
        r={row.isCurrent ? 4 : 3}
        strokeWidth={1.5}
        className={cn(
          'fill-background',
          row.isActive ? 'stroke-primary' : 'stroke-muted-foreground/60',
          row.isCurrent && 'fill-primary',
        )}
      />
    </svg>
  );
}

/** Shared lanes and dots, with each caller supplying its row content/action. */
export function HistoryGraph<N>({
  graph,
  rowKey,
  renderRow,
  label,
  className,
  rowHeight = ROW_HEIGHT,
  laneWidth = LANE_WIDTH,
}: {
  graph: HistoryGraphLayout<N>;
  rowKey: (row: HistoryGraphRow<N>) => Key;
  renderRow: (row: HistoryGraphRow<N>, cell: ReactNode) => ReactNode;
  label: string;
  className?: string;
  /** Keep row content at this height so lane segments remain continuous. */
  rowHeight?: number;
  laneWidth?: number;
}) {
  return (
    <ol aria-label={label} className={className}>
      {graph.rows.map((row) => (
        <li key={rowKey(row)}>
          {renderRow(
            row,
            <HistoryGraphCell
              row={row}
              laneCount={graph.laneCount}
              rowHeight={rowHeight}
              laneWidth={laneWidth}
            />,
          )}
        </li>
      ))}
    </ol>
  );
}
